import React, { useState, useEffect } from 'react';
import { useData } from '../context/DataContext';
import './Orders.css';

const Orders = () => {
  const { orders, ordersLoading, updateOrder, deleteOrder, fetchOrders } = useData();
  
  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('Pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState('');

  const itemsPerPage = 10;
  const totalPages = Math.ceil(orders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = orders.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const handleView = (order) => {
    setViewingOrder(order);
  };

  const closeModal = () => {
    setViewingOrder(null);
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setSelectedStatus(order.status || 'Pending');
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    const result = await updateOrder(orderId, { status: newStatus });
    if (result.success) {
      setEditingOrder(null);
      // Update the viewing order if it's the same order
      if (viewingOrder && (viewingOrder._id || viewingOrder.id) === orderId) {
        setViewingOrder({ ...viewingOrder, status: newStatus });
      }
    } else {
      setError(result.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      const result = await deleteOrder(orderId);
      if (!result.success) {
        setError(result.message);
        setTimeout(() => setError(''), 3000);
      } else {
        setViewingOrder(null);
      }
    }
  };

  return (
    <div className="orders-page">
      <h1 className="page-title">Orders</h1>
      
      {error && <div className="error-message" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>{error}</div>}
      
      {ordersLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading orders...</div>
      ) : (
      <div className="orders-table-container">
        <div className="orders-table-wrapper">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Product Name</th>
              <th>Weight</th>
              <th>Quantity</th>
              <th>Price</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentOrders.map((order) => {
              const orderId = order._id || order.id;
              return (
              <tr key={orderId}>
                <td>#{orderId.slice(-8)}</td>
                <td>{order.productName}</td>
                <td>{order.variantWeight ? `${order.variantWeight}${order.variantUnit}` : 'N/A'}</td>
                <td>{order.quantity}</td>
                <td>₹{order.price.toFixed(2)}</td>
                <td>{order.customerName}</td>
                <td>{new Date(order.date || order.createdAt).toLocaleDateString()}</td>
                <td>
                  <span className={`status-badge ${order.status.toLowerCase()}`}>
                    {order.status}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-view" onClick={() => handleView(order)}>
                      👁️ View
                    </button>
                    <button 
                      className="btn-edit" 
                      onClick={() => handleEdit(order)}
                      disabled={order.status === 'Delivered'}
                      style={{ 
                        opacity: order.status === 'Delivered' ? 0.5 : 1,
                        cursor: order.status === 'Delivered' ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ✏️ Edit
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button 
              className="pagination-btn" 
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>
            <div className="pagination-numbers">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  className={`pagination-number ${currentPage === index + 1 ? 'active' : ''}`}
                  onClick={() => goToPage(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            <button 
              className="pagination-btn" 
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
      )}

      {viewingOrder && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="order-details">
              <div className="detail-section">
                <h3>Order Information</h3>
                <div className="detail-row">
                  <span className="detail-label">Order ID:</span>
                  <span className="detail-value">#{(viewingOrder._id || viewingOrder.id).slice(-8)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">{new Date(viewingOrder.date || viewingOrder.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className={`status-badge ${viewingOrder.status.toLowerCase()}`}>
                    {viewingOrder.status}
                  </span>
                </div>
              </div>

              <div className="detail-section">
                <h3>Product Information</h3>
                <div className="detail-row">
                  <span className="detail-label">Product Name:</span>
                  <span className="detail-value">{viewingOrder.productName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Weight:</span>
                  <span className="detail-value">{viewingOrder.variantWeight ? `${viewingOrder.variantWeight}${viewingOrder.variantUnit}` : 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Quantity:</span>
                  <span className="detail-value">{viewingOrder.quantity}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Total Price:</span>
                  <span className="detail-value price">₹{viewingOrder.price.toFixed(2)}</span>
                </div>
              </div>

              <div className="detail-section">
                <h3>Customer Information</h3>
                <div className="detail-row">
                  <span className="detail-label">Customer Name:</span>
                  <span className="detail-value">{viewingOrder.customerName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Mobile Number:</span>
                  <span className="detail-value">{viewingOrder.customerPhone}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Delivery Address:</span>
                  <span className="detail-value">{viewingOrder.customerAddress}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-close" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {editingOrder && (
        <div className="modal-overlay" onClick={() => setEditingOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Update Order Status</h2>
              <button className="modal-close" onClick={() => setEditingOrder(null)}>×</button>
            </div>
            <div className="order-details">
              <div className="detail-row">
                <span className="detail-label">Order ID:</span>
                <span className="detail-value">#{(editingOrder._id || editingOrder.id).slice(-8)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Product:</span>
                <span className="detail-value">{editingOrder.productName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Weight:</span>
                <span className="detail-value">{editingOrder.variantWeight ? `${editingOrder.variantWeight}${editingOrder.variantUnit}` : 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Customer:</span>
                <span className="detail-value">{editingOrder.customerName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Current Status:</span>
                <span className={`status-badge ${editingOrder.status.toLowerCase()}`}>
                  {editingOrder.status}
                </span>
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Update Status:</label>
                <select 
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '10px', 
                    fontSize: '16px', 
                    borderRadius: '4px', 
                    border: '1px solid #ddd',
                    marginBottom: '15px'
                  }}
                >
                  <option value="Pending">Pending</option>
                  <option value="Processing">Processing</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-close" onClick={() => setEditingOrder(null)}>Cancel</button>
              <button 
                className="btn-submit" 
                onClick={() => handleStatusUpdate(editingOrder._id || editingOrder.id, selectedStatus)}
                style={{ 
                  padding: '10px 20px', 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  marginLeft: '10px'
                }}
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
